<template>
  <v-card>
    <v-card-title class="headline">{{ cardTitle }}</v-card-title>
    <v-divider></v-divider>
    <v-container>
      <p v-if="action === 'delete'" class="title ml-3">
        Are you sure?
      </p>
      <v-form v-else>
        <v-menu
          ref="datePickerMenu"
          v-model="datePickerMenu"
          :close-on-content-click="false"
          :return-value.sync="record.date"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="record.date"
              label="Date"
              prepend-icon="mdi-calendar"
              readonly
              v-on="on"
              :error-messages="formErrors.date"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="record.date"
            :allowed-dates="isDateAllowed"
            no-title
            scrollable
            first-day-of-week="1"
            color="primary"
            @change="$refs.datePickerMenu.save(record.date)"
          >
          </v-date-picker>
        </v-menu>

        <v-row>
          <v-col>
            <v-rating
              length="5"
              v-model="record.happiness"
              empty-icon="far fa-smile"
              full-icon="far fa-smile"
              color="primary"
              background-color="grey lighten-2"
              dense
              large
              class="mb-4"
            ></v-rating>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            cols="12"
            lg="6"
            v-for="marker in $store.state.moodMarkers"
            :key="marker.name"
            class="ma-0 pt-0 pb-0"
          >
            <v-checkbox
              v-model="record[marker.name]"
              :id="marker.name"
              :name="marker.name"
              :error-messages="formErrors[marker.name]"
              :label="marker.tooltipText"
              class="ma-0 pa-0"
            ></v-checkbox>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-for="metric in $store.getters.moodMetrics"
            :key="metric.name"
            lg="3"
            md="6"
            cols="12"
          >
            <p class="caption ma-0">
              {{ metric.helpText }}
            </p>
            <v-rating
              length="5"
              v-model="record[metric.name]"
              :empty-icon="metric.emptyIcon"
              :full-icon="metric.fullIcon"
              :color="metric.color"
              background-color="grey lighten-2"
              dense
              class="mb-2"
            ></v-rating>
          </v-col>
        </v-row>

        <v-alert v-if="formErrors.non_field_errors" type="error" text dense>{{
          formErrors.non_field_errors[0]
        }}</v-alert>
      </v-form>
    </v-container>
    <v-card-actions>
      <v-spacer />
      <v-btn text @click="closeDialog">Cancel</v-btn>
      <v-btn
        text
        :color="action === 'delete' ? 'error' : 'primary'"
        @click="submitForm"
        :loading="isWaitingForResponse"
        >{{ action === "delete" ? "delete" : "save" }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "MoodDiaryForm",

  props: {
    recordedDates: Array,
    action: {
      type: String,
      required: true,
      validator: function (value) {
        return ['create', 'edit', 'delete'].includes(value)
      }
    },
    propsRecord: {
      type: Object,
      default: function () {
        return {
          date: moment().format("YYYY-MM-DD"),
          didFollowKetoDiet: false,
          didWorkout: false,
          didGoForAWalk: false,
          didGoOut: false,
          didTalkToSomeone: false,
          didMeetWithSomeone: false,
          hadFunWithFriends: false,
          hadFunAlone: false,
          hadIntimateExperience: false,
          didMasturbate: false,
          didReadABook: false,
          skinIssues: 3,
          healthIssues: 3,
          sleepQuality: 3,
          dietSatisfaction: 3,
          trainingSatisfaction: 3,
          productivitySatisfaction: 3,
          happiness: 3,
          energy: 3,
          frustration: 3,
          anxiety: 3,
          confidence: 3,
          loneliness: 3,
          optimism: 3,
        }
      }
    }
  },

  data: function () {
    return {
      record: this.propsRecord,
      datePickerMenu: false,
      formErrors: {
      },
      isWaitingForResponse: false
    }
  },

  methods: {
    closeDialog: function () {
      this.$emit('close-dialog')
    },
    isDateAllowed: function (date) {
      return !(this.recordedDates.includes(date))
    },
    submitForm: function () {
      this.isWaitingForResponse = true
      const submitMethods = {
        "create": this.createRecord,
        "edit": this.editRecord,
        "delete": this.deleteRecord
      }
      submitMethods[this.action]()
    },
    createRecord: function () {
      axios
        .post(
          `${process.env.VUE_APP_API_ROOT_URL}mood/records/`,
          this.formData,
          this.$store.getters.axiosConfig
        )
        .then((response) => {
          this.$store.commit("createMoodDiaryRecord", response.data)
          this.isWaitingForResponse = false
          this.closeDialog()
        })
        .catch(error => {
          this.formErrors = error.response.data
          this.isWaitingForResponse = false
        });
    },
    editRecord: function () {
      axios
        .put(
          `${process.env.VUE_APP_API_ROOT_URL}mood/records/${this.record.id}/`,
          this.formData,
          this.$store.getters.axiosConfig
        )
        .then((response) => {
          this.$store.commit("updateMoodDiaryRecord", response.data)
          this.isWaitingForResponse = false
          this.closeDialog()
        })
        .catch(error => {
          this.formErrors = error.response.data
          this.isWaitingForResponse = false
        });
    },
    deleteRecord: function () {
      axios
        .delete(
          `${process.env.VUE_APP_API_ROOT_URL}mood/records/${this.record.id}/`,
          this.$store.getters.axiosConfig
        )
        .then(() => {
          this.$store.commit("deleteMoodDiaryRecord", this.record)
          this.isWaitingForResponse = false
          this.closeDialog()
        })
        .catch(() => {
          this.isWaitingForResponse = false
        });
    }
  },

  computed: {
    cardTitle: function () {
      const cardTitles = {
        "create": "New Record",
        "edit": "Edit Record",
        "delete": "Delete Record",
      }
      return cardTitles[this.action]
    },
    moodMetrics: function () {
      const markers = this.$store.state.moodMarkers
      const conditions = this.$store.state.moodConditions
      const ratings = this.$store.state.moodRatings
      return markers.concat(conditions, ratings)
    },
    formFields: function () {
      const baseFields = this.moodMetrics.map(metric => metric.name)
      const additionalFields = ["date", "happiness"]
      return baseFields.concat(additionalFields)
    },
    formData: function () {
      const formData = this.formFields.reduce((formData, fieldName) => {
        formData[fieldName] = this.$data.record[fieldName]
        return formData
      }, {})
      return formData
    }
  },
}
</script>